import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StoryCard from "../components/StoryCard";
import { BookOpen, PenLine, Search, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Stories() {
  const { user } = useAuth();
  const isStaff = user && (user.role === "admin" || user.role === "moderator");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 60 };
      if (activeCat) params.category = activeCat;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/stories", { params });
      setItems(data.items || []);
      if (data.categories) setCategories(data.categories);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load stories");
    } finally {
      setLoading(false);
    }
  }, [sort, activeCat, q]);

  useEffect(() => { load(); }, [sort, activeCat]); // eslint-disable-line

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-6 pb-16" data-testid="stories-page">
      <div className="relative rounded-3xl overflow-hidden mb-6 border border-white/[0.08] bg-gradient-to-br from-[#00E5FF]/15 via-[#0F1117] to-[#0066FF]/15 p-7 sm:p-9">
        <div className="absolute -right-12 -top-14 w-64 h-64 rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] mb-3"><BookOpen size={13} /> Fiction &amp; Histories</span>
            <h1 className="font-heading text-4xl sm:text-6xl text-white tracking-tight">Stories</h1>
            <p className="text-[#9CA3AF] text-sm mt-3 max-w-2xl">Erotic fiction &amp; personal histories — a living archive of queer voices.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isStaff && (
              <Link to="/admin/stories" data-testid="stories-review-link" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F1A817]/40 text-[#F1A817] hover:bg-[#F1A817]/10 font-semibold text-sm transition-colors">
                <ShieldCheck size={16} /> Review queue
              </Link>
            )}
            {user && (
              <Link to="/stories/upload" data-testid="stories-write-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-xs hover:bg-[#00c9e0] transition-colors">
                <PenLine size={16} /> Add a story
              </Link>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={16} />
          <input
            data-testid="stories-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, themes, kinks…"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-[#52525B]"
          />
        </div>
        <select
          data-testid="stories-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#00E5FF]"
        >
          <option value="recent">Newest</option>
          <option value="popular">Most liked</option>
        </select>
      </form>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="stories-categories">
        <button onClick={() => setActiveCat("")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${activeCat === "" ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-[#A1A1AA] border-[#1C1C1F] hover:border-[#00E5FF]"}`}>All</button>
        {categories.map((c) => (
          <button key={c} data-testid={`stories-cat-${c}`} onClick={() => setActiveCat(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${activeCat === c ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-[#A1A1AA] border-[#1C1C1F] hover:border-[#00E5FF]"}`}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-[#52525B]" data-testid="stories-loading">
          <Loader2 className="animate-spin text-[#00E5FF] mb-3" size={32} />
        </div>
      ) : items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stories-grid">
          {items.map((s, i) => <StoryCard key={s.id} story={s} index={i} />)}
        </div>
      ) : (
        <div className="border border-dashed border-[#1C1C1F] rounded-2xl py-20 text-center" data-testid="stories-empty">
          <BookOpen className="text-[#1C1C1F] mx-auto mb-3" size={44} />
          <p className="text-[#52525B] font-mono text-xs uppercase tracking-widest mb-4">No stories yet</p>
          {user && <Link to="/stories/upload" className="text-[#00E5FF] hover:text-white text-sm font-semibold">Be the first to add one →</Link>}
        </div>
      )}
    </div>
  );
}
